import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { api, STATUS_TO_PILL } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";

const fetcher = (u) => api.get(u).then(r=>r.data);

/**
 * Generic CRUD table with create + edit + soft delete.
 * idField required to distinguish rows for edit/delete.
 */
export function CrudList({ title, endpoint, columns, formFields, testidPrefix, statusField, idField, canDelete=true }) {
  const { data, isLoading } = useSWR(endpoint, fetcher);
  const [modal, setModal] = useState(null); // { mode: "create"|"edit", row }
  const [form, setForm] = useState({});
  const [q, setQ] = useState("");

  const filtered = (data||[]).filter(r => {
    if (!q) return true;
    return Object.values(r).some(v => String(v||"").toLowerCase().includes(q.toLowerCase()));
  });

  const openCreate = () => { setForm({}); setModal({ mode:"create" }); };
  const openEdit = (row) => {
    const only = {};
    formFields.forEach(f => { if (row[f.key] !== undefined) only[f.key] = row[f.key]; });
    setForm(only); setModal({ mode:"edit", row });
  };

  const submit = async () => {
    try {
      if (modal.mode === "create") {
        await api.post(endpoint, form);
        toast.success("Created");
      } else {
        await api.patch(`${endpoint}/${modal.row[idField]}`, form);
        toast.success("Saved");
      }
      setModal(null); setForm({});
      mutate(endpoint);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const del = async (row) => {
    if (!confirm(`Delete this ${title.replace(/s$/,"")}?`)) return;
    try {
      await api.delete(`${endpoint}/${row[idField]}`);
      toast.success("Deleted");
      mutate(endpoint);
    } catch(e){ toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4" data-testid={`${testidPrefix}-page`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
          <p className="text-sm text-slate-500">{filtered.length} records · click any row to edit</p>
        </div>
        <div className="flex gap-2">
          <input data-testid={`${testidPrefix}-search`} value={q} onChange={e=>setQ(e.target.value)} placeholder="Search..." className="text-sm px-3 py-1.5 rounded-md border border-slate-200 bg-white"/>
          <button data-testid={`btn-create-${testidPrefix}`} onClick={openCreate} className="bg-sky-600 hover:bg-sky-700 text-white text-sm px-3 py-1.5 rounded-md">+ New</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-auto max-h-[65vh]">
          <table className="data-table" data-testid={`${testidPrefix}-table`}>
            <thead><tr>{columns.map(c=><th key={c.key}>{c.label}</th>)}<th></th></tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={columns.length+1} className="text-center text-slate-400 py-6">Loading...</td></tr>}
              {!isLoading && filtered.length===0 && <tr><td colSpan={columns.length+1} className="text-center text-slate-400 py-6">No records</td></tr>}
              {filtered.map((r)=>(
                <tr key={r[idField]||r.name||r.code} data-testid={`${testidPrefix}-row-${r[idField]||r.name||r.code}`} onDoubleClick={()=>openEdit(r)} className="cursor-pointer">
                  {columns.map(c=>(
                    <td key={c.key}>
                      {c.render ? c.render(r) :
                       c.key===statusField ? <span className={`pill ${STATUS_TO_PILL(r[c.key])}`}>{r[c.key]}</span> :
                       String(r[c.key] ?? "")}
                    </td>
                  ))}
                  <td className="text-right whitespace-nowrap">
                    <button data-testid={`btn-edit-${testidPrefix}-${r[idField]||r.name}`} onClick={()=>openEdit(r)} className="text-xs px-2 py-1 text-sky-600 hover:bg-sky-50 rounded" title="Edit"><Pencil size={12}/></button>
                    {canDelete && <button data-testid={`btn-delete-${testidPrefix}-${r[idField]||r.name}`} onClick={()=>del(r)} className="text-xs px-2 py-1 text-red-500 hover:bg-red-50 rounded" title="Delete"><Trash2 size={12}/></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl p-5 max-h-[90vh] overflow-y-auto" data-testid={`${testidPrefix}-modal`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">{modal.mode==="create" ? "Create" : "Edit"} {title.replace(/s$/,"")}</h3>
              <button onClick={()=>setModal(null)} className="text-slate-400 hover:text-slate-900">✕</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {formFields.map(f=>(
                <div key={f.key} className={f.full ? "col-span-2":""}>
                  <label className="text-[11px] uppercase text-slate-500 font-semibold">{f.label}{f.required && " *"}</label>
                  {f.type==="select" ? (
                    <select data-testid={`input-${testidPrefix}-${f.key}`} value={form[f.key]||""} onChange={e=>setForm({...form,[f.key]:e.target.value})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5">
                      <option value="">Select...</option>
                      {f.options?.map(o => <option key={typeof o==="string"?o:o.value} value={typeof o==="string"?o:o.value}>{typeof o==="string"?o:o.label}</option>)}
                    </select>
                  ) : f.type==="multiselect" ? (
                    <select data-testid={`input-${testidPrefix}-${f.key}`} multiple size={Math.min(5, (f.options?.length||3))}
                      value={Array.isArray(form[f.key]) ? form[f.key] : []}
                      onChange={e=>setForm({...form, [f.key]: Array.from(e.target.selectedOptions).map(o=>o.value)})}
                      className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5">
                      {f.options?.map(o => <option key={typeof o==="string"?o:o.value} value={typeof o==="string"?o:o.value}>{typeof o==="string"?o:o.label}</option>)}
                    </select>
                  ) : f.type==="textarea" ? (
                    <textarea data-testid={`input-${testidPrefix}-${f.key}`} value={form[f.key]||""} onChange={e=>setForm({...form,[f.key]:e.target.value})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5" rows={2}/>
                  ) : (
                    <input data-testid={`input-${testidPrefix}-${f.key}`} type={f.type||"text"} value={form[f.key] ?? ""} onChange={e=>setForm({...form,[f.key]: f.type==="number"?Number(e.target.value):e.target.value})} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"/>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={()=>setModal(null)} className="text-sm px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
              <button data-testid={`btn-submit-${testidPrefix}`} onClick={submit} className="text-sm px-3 py-1.5 rounded-md bg-sky-600 text-white">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
